/* Back to the line — the August 2026 South Australian poll.
   /intelligence/back-to-the-line/

   Draws each panel's chart over the table the build already wrote, and wires
   the panel tools. Everything drawn is read from #sa-aug-data, the derived
   block src/_data/saAugust.js renders from sa-poll-august-2026.json, so no
   row, label or event is defined twice. The one piece of arithmetic that runs
   here as well as at build is the preference scenario.

   The page wears the v6 house components (styles-v6.css): the panel tools are
   the Victorian tracker's pills, the tooltip is its tooltip, the lever is laid
   out as its lever is, and chart text wears its text classes.

   One reading model on every chart: a mark shows its figures on hover with a
   mouse, on tap on a touch screen, and on focus from the keyboard, in the same
   tooltip, built by the same function. Every figure a tooltip shows is also
   in the panel's table.

   One encoding on every chart: the election result is an ink line, June is a
   ring or an outline, August is a filled mark. */
(function () {
  "use strict";
  const ROOT = document.getElementById("sa-aug");
  const DATA_EL = document.getElementById("sa-aug-data");
  if (!ROOT || !DATA_EL) return;
  let C;
  try { C = JSON.parse(DATA_EL.textContent); } catch (e) { ROOT.classList.remove("sa-js"); return; }

  const P = C.parties, SHORT = C.short, KEY = C.key, MOE = C.moe;
  const [E, J, A] = C.waves;
  const col = p => `var(--p-${KEY[p]})`;
  const SVG_NS = "http://www.w3.org/2000/svg";
  const MINUS = "−", EN = "–";
  const phone = window.matchMedia("(max-width: 599px)");
  const under640 = window.matchMedia("(max-width: 639px)");
  const under760 = window.matchMedia("(max-width: 760px)");

  /* ── helpers ── */
  const $ = (q, r) => (r || document).querySelector(q);
  const $$ = (q, r) => Array.from((r || document).querySelectorAll(q));
  function el(tag, attrs, kids) {
    const n = document.createElement(tag);
    Object.entries(attrs || {}).forEach(([k, v]) => { if (k === "text") n.textContent = v; else if (v != null) n.setAttribute(k, v); });
    (kids || []).forEach(c => n.appendChild(typeof c === "string" ? document.createTextNode(c) : c));
    return n;
  }
  function s(tag, attrs, parent) {
    const n = document.createElementNS(SVG_NS, tag);
    Object.entries(attrs || {}).forEach(([k, v]) => { if (k === "text") n.textContent = v; else if (v != null) n.setAttribute(k, v); });
    if (parent) parent.appendChild(n);
    return n;
  }
  const r1 = v => Math.round(v * 10) / 10;
  const num = v => (Number.isInteger(r1(v)) ? String(r1(v)) : r1(v).toFixed(1));
  const signed = v => { const x = r1(v); return x > 0 ? "+" + num(x) : x < 0 ? MINUS + num(-x) : "0"; };
  const pct = v => num(v) + "%";
  const ceilTo = (v, step) => Math.ceil(v / step) * step;
  const floorTo = (v, step) => Math.floor(v / step) * step;
  const ticks = (lo, hi, step) => { const t = []; for (let v = lo; v <= hi + 1e-9; v += step) t.push(v); return t; };
  const trend = d => (d > 0 ? "up" : d < 0 ? "down" : "flat");

  const LIVE = $("#viz-live"), TOAST = $("#sa-toast");
  let liveT, toastT;
  function announce(t) { if (!LIVE) return; clearTimeout(liveT); liveT = setTimeout(() => { LIVE.textContent = t; }, 250); }
  function say(t) { if (!TOAST) return; TOAST.textContent = t; TOAST.classList.add("show"); clearTimeout(toastT); toastT = setTimeout(() => TOAST.classList.remove("show"), 2400); }

  /* ══════════ the tooltip: the v6 tooltip, one reading model ══════════
     content = { head, sub, rows: [[label, value, colour?]], flag } */
  const Tip = (function () {
    const box = $("#sa-tip");
    let current = null, pinned = false;
    function render(c) {
      box.textContent = "";
      if (c.head) box.appendChild(el("div", { class: "tip-h", text: c.head }));
      if (c.sub) box.appendChild(el("div", { class: "tip-s", text: c.sub }));
      (c.rows || []).forEach(([label, value, colour]) => {
        const k = el("span", { class: "k" });
        if (colour) { const i = el("i"); i.style.background = colour; k.appendChild(i); }
        k.appendChild(document.createTextNode(label));
        box.appendChild(el("div", { class: "tip-row" }, [k, el("span", { class: "n", text: value })]));
      });
      if (c.flag) box.appendChild(el("div", { class: "tip-flag", text: c.flag }));
    }
    function place(x, y) {
      const w = box.offsetWidth, h = box.offsetHeight, pad = 12;
      let left = x + 14, top = y - h - 12;
      if (left + w > window.innerWidth - pad) left = x - w - 14;
      if (left < pad) left = Math.max(pad, Math.min(window.innerWidth - w - pad, x - w / 2));
      if (top < pad) top = y + 18;
      box.style.left = Math.round(left) + "px"; box.style.top = Math.round(top) + "px";
    }
    function anchorOf(node) { const r = node.getBoundingClientRect(); return [r.left + r.width / 2, r.top + Math.min(r.height / 2, 12)]; }
    function show(node, content, x, y, pin) {
      if (current && current !== node) current.classList.remove("on");
      current = node; pinned = !!pin;
      node.classList.add("on");
      render(content); box.hidden = false; box.classList.add("show");
      if (x == null) [x, y] = anchorOf(node);
      place(x, y);
    }
    function hide() { box.classList.remove("show"); box.hidden = true; if (current) current.classList.remove("on"); current = null; pinned = false; }
    document.addEventListener("pointerdown", e => { if (current && !current.contains(e.target)) hide(); }, true);
    document.addEventListener("keydown", e => { if (e.key === "Escape" && current) hide(); });
    window.addEventListener("scroll", () => { if (current) { if (pinned || document.activeElement === current) { const [x, y] = anchorOf(current); place(x, y); } else hide(); } }, { passive: true });
    /* A mark: named for assistive technology, reachable by Tab when it is a
       focus unit, and read on hover, tap or focus. */
    function bind(node, label, build, focusable) {
      node.classList.add("mk");
      if (focusable !== false) { node.setAttribute("tabindex", "0"); node.setAttribute("role", "img"); node.setAttribute("aria-label", label); }
      node.addEventListener("pointerenter", e => { if (e.pointerType !== "touch" && !pinned) show(node, build(), e.clientX, e.clientY); });
      node.addEventListener("pointermove", e => { if (e.pointerType !== "touch" && current === node && !pinned) place(e.clientX, e.clientY); });
      node.addEventListener("pointerleave", e => { if (e.pointerType !== "touch" && current === node && !pinned) hide(); });
      node.addEventListener("pointerup", e => {
        if (e.pointerType !== "touch") return;
        e.stopPropagation();
        if (current === node && pinned) hide(); else show(node, build(), null, null, true);
      });
      if (focusable !== false) {
        node.addEventListener("focus", () => show(node, build()));
        node.addEventListener("blur", () => { if (current === node) hide(); });
      }
    }
    return { bind, hide };
  })();

  /* ══════════ panel tools: link · png · csv · table ══════════ */
  const CANON = (function () { const c = $('link[rel="canonical"]'); return (c && c.getAttribute("href")) || location.origin + location.pathname; })();
  const WHERE = CANON.replace(/^https?:\/\//, "");
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text).then(() => true, fallback);
    return Promise.resolve(fallback());
    function fallback() {
      const ta = el("textarea", { style: "position:fixed;left:-9999px;top:0", "aria-hidden": "true" });
      ta.value = text; document.body.appendChild(ta); ta.select();
      let ok = false; try { ok = document.execCommand("copy"); } catch (e) {}
      ta.remove(); return ok;
    }
  }
  function download(blob, name) {
    const a = el("a", { href: URL.createObjectURL(blob), download: name, style: "display:none" });
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
    say("Saved " + name);
  }
  const textOf = (panel, q) => { const n = $(q, panel); return n ? n.textContent.replace(/\s+/g, " ").trim() : ""; };
  function toCsv(panel) {
    const q = v => { const t = String(v == null ? "" : v); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
    const cell = n => n.textContent.replace(/\s+/g, " ").trim();
    const lines = [];
    $$(".panel-table table", panel).forEach((t, i) => {
      if (i) lines.push("");
      const cap = $("caption", t); if (cap) lines.push(q(cell(cap)));
      lines.push($$("thead th", t).map(n => q(cell(n))).join(","));
      $$("tbody tr", t).forEach(tr => lines.push(Array.from(tr.children).map(n => q(cell(n))).join(",")));
    });
    lines.push("", q("Source: " + textOf(panel, ".panel-src") + " · Analysis and visualisation by Komms-Haus · Ace Strategies · " + WHERE));
    return new Blob([lines.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
  }
  const STYLE_PROPS = ["fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray", "stroke-linecap", "stroke-linejoin", "stroke-opacity", "opacity", "font-family", "font-size", "font-weight", "letter-spacing", "text-anchor", "text-transform", "dominant-baseline", "paint-order"];
  function inline(src, dst) {
    const cs = getComputedStyle(src); let st = "";
    STYLE_PROPS.forEach(k => { const v = cs.getPropertyValue(k); if (v) st += `${k}:${v};`; });
    dst.setAttribute("style", st);
    if (src.tagName === "text" && cs.textTransform === "uppercase") dst.textContent = src.textContent.toUpperCase();
    for (let i = 0; i < src.children.length && i < dst.children.length; i++) inline(src.children[i], dst.children[i]);
  }
  function wrap(ctx, text, x, y, maxW, lh) {
    const words = String(text).split(/\s+/); let line = "", yy = y;
    words.forEach(w => { const t = line ? line + " " + w : w; if (ctx.measureText(t).width > maxW && line) { ctx.fillText(line, x, yy); line = w; yy += lh; } else line = t; });
    if (line) { ctx.fillText(line, x, yy); yy += lh; }
    return yy;
  }
  function toPng(panel, extra) {
    const svg = $(".sa-chart svg", panel);
    if (!svg) { say("This panel has no chart to save"); return; }
    const W = +svg.getAttribute("width"), H = +svg.getAttribute("height");
    const clone = svg.cloneNode(true); inline(svg, clone);
    clone.setAttribute("xmlns", SVG_NS);
    const cs = getComputedStyle(ROOT);
    const bg = cs.getPropertyValue("--bg").trim() || "#050813", ink = cs.getPropertyValue("--ink-bold").trim() || "#fff", muted = cs.getPropertyValue("--muted").trim() || "#93a5bd";
    const pad = 28, scale = 2, cv = document.createElement("canvas"), ctx = cv.getContext("2d");
    const lines = [["600 18px 'IBM Plex Sans', sans-serif", ink, textOf(panel, ".panel-title"), 23], ["400 12.5px 'IBM Plex Sans', sans-serif", muted, textOf(panel, ".panel-sub"), 17]];
    if (extra) lines.push(["600 12.5px 'IBM Plex Sans', sans-serif", ink, extra, 17]);
    const foot = [["500 11px 'IBM Plex Sans', sans-serif", muted, textOf(panel, ".panel-src"), 14], ["500 11px 'IBM Plex Sans', sans-serif", muted, "Analysis and visualisation by Komms-Haus · Ace Strategies · " + WHERE, 14]];
    const measure = set => set.reduce((h, [f, , t, lh]) => { ctx.font = f; let n = 1, line = ""; String(t).split(/\s+/).forEach(w => { const x = line ? line + " " + w : w; if (ctx.measureText(x).width > W && line) { n++; line = w; } else line = x; }); return h + n * lh + 4; }, 0);
    const headH = measure(lines) + 10, footH = measure(foot) + 10;
    cv.width = (W + pad * 2) * scale; cv.height = (pad * 2 + headH + H + footH) * scale;
    ctx.scale(scale, scale); ctx.fillStyle = bg; ctx.fillRect(0, 0, W + pad * 2, pad * 2 + headH + H + footH);
    ctx.textBaseline = "top";
    let y = pad; lines.forEach(([f, c, t, lh]) => { ctx.font = f; ctx.fillStyle = c; y = wrap(ctx, t, pad, y, W, lh) + 4; });
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" }));
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, pad, pad + headH, W, H);
      let yy = pad + headH + H + 10; foot.forEach(([f, c, t, lh]) => { ctx.font = f; ctx.fillStyle = c; yy = wrap(ctx, t, pad, yy, W, lh) + 4; });
      URL.revokeObjectURL(url);
      cv.toBlob(b => { if (b) download(b, `sa-poll-august-2026-${panel.id}.png`); else say("The chart could not be saved"); }, "image/png");
    };
    img.onerror = () => { URL.revokeObjectURL(url); say("The chart could not be saved"); };
    img.src = url;
  }
  const PNG_EXTRA = {};
  function toolBtn(act, glyph, word, title, onClick) {
    const b = el("button", { class: "tbl-btn", type: "button", title, "aria-label": title, "data-act": act });
    b.appendChild(el("span", { text: glyph }));
    b.appendChild(el("span", { text: word }));
    b.addEventListener("click", onClick);
    return b;
  }
  $$(".panel[data-sa-panel]", ROOT).forEach(panel => {
    const top = $(".panel-top", panel), id = panel.id;
    if (!top) return;
    const tools = el("div", { class: "panel-tools" });
    tools.appendChild(toolBtn("link", "⛓", "link", "Copy a link to this panel", () => copyText(CANON + query() + "#" + id).then(ok => say(ok ? "Link copied" : "The link could not be copied"))));
    if ($(".sa-chart", panel) && panel.getAttribute("data-chart") !== "none") tools.appendChild(toolBtn("png", "⇩", "png", "Save this chart as a PNG", () => toPng(panel, PNG_EXTRA[id] ? PNG_EXTRA[id]() : "")));
    tools.appendChild(toolBtn("csv", "≡", "csv", "Save this panel's figures as a CSV", () => download(toCsv(panel), `sa-poll-august-2026-${id}.csv`)));
    const tbl = el("button", { class: "tbl-btn", type: "button", "aria-pressed": "false", title: "Show the figures as a table", "data-act": "table" });
    tbl.appendChild(el("span", { text: "⊞" })); tbl.appendChild(el("span", { text: "table" }));
    tbl.addEventListener("click", () => { const on = panel.classList.toggle("show-table"); tbl.setAttribute("aria-pressed", on ? "true" : "false"); tbl.lastChild.textContent = on ? "chart" : "table"; });
    tools.appendChild(tbl);
    top.appendChild(tools);
  });

  /* ── the lever's settings in the address ── */
  const params = new URLSearchParams(location.search);
  const readFlow = k => { const raw = params.get(k); if (raw == null || !/^\d{1,3}(\.\d)?$/.test(raw)) return null; const v = +raw; return v >= 0 && v <= 100 ? v : null; };
  const flows = {
    LIB: readFlow("lib") ?? C.defaultFlows.LIB,
    GRN: readFlow("grn") ?? C.defaultFlows.GRN,
    OTH: readFlow("oth") ?? C.defaultFlows.OTH,
  };
  const atDefault = () => ["LIB", "GRN", "OTH"].every(k => flows[k] === C.defaultFlows[k]);
  function query() { if (atDefault()) return ""; return `?lib=${num(flows.LIB)}&grn=${num(flows.GRN)}&oth=${num(flows.OTH)}`; }
  let qT;
  function writeQuery() { clearTimeout(qT); qT = setTimeout(() => { try { history.replaceState(null, "", location.pathname + query() + location.hash); } catch (e) {} }, 150); }
  const scenario = f => r1(C.primary.ALP + C.primary.LIB * f.LIB / 100 + C.primary.GRN * f.GRN / 100 + C.primary.OTH * f.OTH / 100);

  /* ── the root of a chart ── */
  function root(holder, W, H, id) {
    holder.textContent = "";
    return s("svg", { class: "sa-svg", viewBox: `0 0 ${W} ${H}`, width: W, height: H, role: "group", "aria-labelledby": `${id}-title`, "aria-describedby": `${id}-cap` }, holder);
  }
  const widthOf = holder => Math.max(280, Math.round(holder.clientWidth));
  /* the three marks of the one encoding, on a row */
  function marks(g, x, e, j, a, colour, r) {
    r = r || 5.5;
    s("line", { class: "link", x1: x(j), x2: x(a), y1: 0, y2: 0, stroke: colour, opacity: 0.55 }, g);
    if (e != null) s("line", { class: "elect", x1: x(e), x2: x(e), y1: -11, y2: 11 }, g);
    s("circle", { class: "ring", cx: x(j), cy: 0, r: r - 1, stroke: colour }, g);
    s("circle", { class: "dot", cx: x(a), cy: 0, r, fill: colour }, g);
  }
  const SUB = "A subgroup reading, with a margin wider than ±" + num(MOE) + ".";

  const CHARTS = {};

  /* ══════════ 1 · the line: primary vote and two-party ══════════ */
  CHARTS["panel-1"] = function (holder) {
    const W = widthOf(holder), narrow = phone.matches;
    const labW = narrow ? 76 : 110, valW = narrow ? 44 : 150, rowH = narrow ? 54 : 46, top = 20;
    const hi = ceilTo(Math.max(...P.map(p => Math.max(A.primary[p] + MOE, E.primary[p], J.primary[p]))), 5);
    const x = v => labW + v / hi * (W - labW - valW - 8);
    const bottom = top + P.length * rowH, axisY = bottom + 16;
    const y0 = axisY + 34, sy = y0 + 26, sh = 14, H = sy + sh + 38;
    const svg = root(holder, W, H, "panel-1");
    ticks(0, hi, 10).forEach(t => {
      s("line", { class: "gridline", x1: x(t), x2: x(t), y1: top, y2: bottom }, svg);
      s("text", { x: x(t), y: axisY, "text-anchor": "middle", class: "axis-txt", text: t === 0 ? "0%" : String(t) }, svg);
    });
    P.forEach((p, i) => {
      const yc = top + i * rowH + rowH / 2, a = A.primary[p], j = J.primary[p], e = E.primary[p];
      const g = s("g", null, svg);
      s("rect", { class: "hit", x: 0, y: yc - rowH / 2, width: W, height: rowH }, g);
      s("circle", { cx: 5, cy: yc, r: 4, fill: col(p) }, g);
      s("text", { x: 16, y: yc, "dominant-baseline": "central", class: "name-lbl hot", text: SHORT[p] }, g);
      s("rect", { class: "band", x: x(a - MOE), y: yc - 7, width: x(a + MOE) - x(a - MOE), height: 14, rx: 3, fill: col(p) }, g);
      const m = s("g", { transform: `translate(0,${yc})` }, g);
      marks(m, x, e, j, a, col(p));
      /* the election above its tick and June below its ring, as the Victorian range chart labels its ends */
      s("text", { x: x(e), y: yc - 14, "text-anchor": "middle", class: "anno", text: `election ${num(e)}` }, g);
      s("text", { x: x(j), y: yc + 19, "text-anchor": "middle", class: "anno", text: `June ${num(j)}` }, g);
      s("text", { x: W, y: narrow ? yc : yc - 5, "dominant-baseline": "central", "text-anchor": "end", class: "val-lbl", text: pct(a) }, g);
      if (!narrow) s("text", { x: W, y: yc + 9, "dominant-baseline": "central", "text-anchor": "end", class: "anno", text: `${signed(a - e)} on the election · ${signed(a - j)} on June` }, g);
      Tip.bind(g, `${SHORT[p]}: August ${num(a)} per cent, June ${num(j)}, election ${num(e)}`, () => ({
        head: SHORT[p], sub: "Primary vote, per cent",
        rows: [["August 2026", pct(a), col(p)], ["June 2026", pct(j)], [`Election, ${C.electionDayMonth}`, pct(e)], ["Since the election", signed(a - e)], ["Since June", signed(a - j)]],
        flag: `The band is the ±${num(MOE)} margin of error on the August reading.`,
      }));
    });
    /* the two-party estimate as a strip on its own 0–100 scale, labelled at both ends */
    const xt = v => v / 100 * W;
    s("text", { x: 0, y: y0, class: "axis-lbl", text: "Two-party preferred · Labor v One Nation" }, svg);
    const g = s("g", null, svg);
    s("rect", { class: "hit", x: 0, y: sy - 6, width: W, height: sh + 40 }, g);
    s("rect", { class: "tug-a", x: 0, y: sy, width: Math.max(0, xt(A.tpp.ALP) - 1), height: sh, rx: 3 }, g);
    s("rect", { class: "tug-o", x: xt(A.tpp.ALP) + 1, y: sy, width: Math.max(0, W - xt(A.tpp.ALP) - 1), height: sh, rx: 3 }, g);
    s("line", { class: "elect", x1: xt(E.tpp.ALP), x2: xt(E.tpp.ALP), y1: sy - 5, y2: sy + sh + 5 }, g);
    s("text", { x: 0, y: sy + sh + 16, class: "val-lbl", text: `Labor ${num(A.tpp.ALP)}` }, g);
    s("text", { x: W, y: sy + sh + 16, "text-anchor": "end", class: "val-lbl", text: `One Nation ${num(A.tpp.ONP)}` }, g);
    s("text", { x: xt(E.tpp.ALP), y: sy - 9, "text-anchor": "middle", class: "anno", text: `election ${num(E.tpp.ALP)}` }, g);
    s("circle", { class: "ring", cx: xt(J.tpp.ALP), cy: sy + sh + 27, r: 4.5, stroke: "var(--ink-bold)" }, g);
    s("text", { x: xt(J.tpp.ALP) - 8, y: sy + sh + 27, "dominant-baseline": "central", "text-anchor": "end", class: "anno", text: `June ${num(J.tpp.ALP)}` }, g);
    Tip.bind(g, `Two-party preferred: August Labor ${num(A.tpp.ALP)}, One Nation ${num(A.tpp.ONP)}; June ${num(J.tpp.ALP)}; election estimate ${num(E.tpp.ALP)}`, () => ({
      head: "Two-party preferred", sub: "Labor v One Nation, DemosAU estimates",
      rows: [["August 2026", `${num(A.tpp.ALP)}${EN}${num(A.tpp.ONP)}`, col("ALP")], ["June 2026", `${num(J.tpp.ALP)}${EN}${num(J.tpp.ONP)}`], ["Election estimate", `${num(E.tpp.ALP)}${EN}${num(E.tpp.ONP)}`]],
      flag: "From flows in seats with a Labor–One Nation final count.",
    }));
  };

  /* ══════════ 2 · the preference lever, laid out as the Victorian one ══════════ */
  CHARTS["panel-2"] = function (holder) {
    holder.textContent = "";
    const SETS = [["LIB", "lib", "Liberal preferences to Labor"], ["GRN", "grn", "Greens preferences to Labor"], ["OTH", "oth", "Others' preferences to Labor"]];
    const split = v => `${num(v)}${EN}${num(100 - v)}`;
    const vtext = v => `${num(v)} per cent to Labor, ${num(100 - v)} to One Nation`;
    const wrapEl = el("div", { class: "lever" }), ctl = el("div", { class: "lever-ctl" }), outs = {}, inputs = {};
    SETS.forEach(([k, id, label]) => {
      const row = el("div", { class: "lever-row " + id });
      const b = el("b", { class: "num", text: split(flows[k]) });
      row.appendChild(el("label", { for: `lever-${id}` }, [el("span", { text: label }), b]));
      const inp = el("input", { type: "range", id: `lever-${id}`, min: "0", max: "100", step: "0.1", value: String(flows[k]), "aria-valuetext": vtext(flows[k]) });
      inp.addEventListener("input", () => { flows[k] = r1(+inp.value); update(); });
      inp.addEventListener("change", () => announce(big.textContent + ", a projection"));
      outs[k] = b; inputs[k] = inp;
      row.appendChild(inp); ctl.appendChild(row);
    });
    const reset = el("button", { type: "button", class: "lever-reset", text: "↺ Reset to DemosAU's March estimates" });
    reset.addEventListener("click", () => { Object.assign(flows, C.defaultFlows); SETS.forEach(([k]) => { inputs[k].value = String(flows[k]); }); update(); announce(big.textContent + ", a projection"); });
    ctl.appendChild(reset);
    wrapEl.appendChild(ctl);
    const out = el("div", { class: "lever-out" });
    out.appendChild(el("div", { class: "lever-kick", text: "Projected two-party preferred · Labor – One Nation" }));
    const big = el("b", { class: "num sa-readout" });
    out.appendChild(el("div", { class: "lever-big" }, [big, el("span", { text: "projection" })]));
    const ta = el("div", { class: "a" }), to = el("div", { class: "o" });
    out.appendChild(el("div", { class: "lever-tug", "aria-hidden": "true" }, [ta, to]));
    const line = el("p", { class: "lever-line" }), swing = el("p", { class: "lever-swing" });
    out.appendChild(line); out.appendChild(swing);
    wrapEl.appendChild(out);
    holder.appendChild(wrapEl);
    function update() {
      const alp = scenario(flows), onp = r1(100 - alp), vs = r1(alp - E.tpp.ALP);
      big.textContent = `${num(alp)}${EN}${num(onp)}`;
      ta.style.width = alp + "%"; to.style.width = onp + "%";
      SETS.forEach(([k]) => { outs[k].textContent = split(flows[k]); inputs[k].setAttribute("aria-valuetext", vtext(flows[k])); });
      line.textContent = "";
      line.appendChild(el("span", null, ["The starting point is ", el("b", { text: P.map(p => `${SHORT[p]} ${num(A.primary[p])}`).join(" · ") }), ", the August primary votes as published. "]));
      line.appendChild(el("span", { class: "pub", text: `DemosAU published ${num(A.tpp.ALP)}${EN}${num(A.tpp.ONP)} for this wave on unrounded shares.` }));
      swing.textContent = "";
      swing.appendChild(el("span", null, ["Against the ", el("b", { text: `${num(E.tpp.ALP)}${EN}${num(E.tpp.ONP)}` }), " election estimate: "]));
      swing.appendChild(el("b", { class: vs >= 0 ? "clear" : "short", text: vs === 0 ? "level" : `${num(Math.abs(vs))} ${vs > 0 ? "above" : "below"}` }));
      writeQuery();
    }
    PNG_EXTRA["panel-2"] = () => `Settings: Liberal ${num(flows.LIB)}, Greens ${num(flows.GRN)}, others ${num(flows.OTH)} per cent to Labor. Labor ${big.textContent}, a projection.`;
    update();
  };

  /* ══════════ 3 · cohorts: every party on every row, one brought forward ══════════ */
  let lit = "ALP";
  function cohortLegend(holder) {
    const box = el("div", { class: "legend", id: "cohort-party", role: "group", "aria-label": "Party to bring forward" });
    P.forEach(p => {
      const b = el("button", { class: "legend-item", type: "button", "aria-pressed": p === lit ? "true" : "false", "data-p": p, "aria-label": `Bring ${SHORT[p]} forward on every row` });
      b.appendChild(el("i", { class: "sw", style: `background:${col(p)}` }));
      b.appendChild(document.createTextNode(SHORT[p]));
      b.addEventListener("click", () => {
        if (lit === p) return;
        lit = p;
        $$("button", box).forEach(x => x.setAttribute("aria-pressed", x.dataset.p === p ? "true" : "false"));
        CHARTS["panel-3"](holder);
        announce(`${SHORT[p]} is brought forward on the cohort chart.`);
      });
      box.appendChild(b);
    });
    return box;
  }
  CHARTS["panel-3"] = function (holder) {
    const narrow = phone.matches, W = widthOf(holder);
    const viz = holder.parentElement;
    if (viz && !$("#cohort-party", viz)) viz.insertBefore(cohortLegend(holder), holder);
    const rowH = narrow ? 46 : 32, groupH = 24, headH = 18;
    const cohorts = C.cohorts, groups = [];
    cohorts.forEach(c => { if (c.group && !groups.includes(c.group)) groups.push(c.group); });
    const H = headH + rowH * cohorts.length + groupH * groups.length;
    const svg = root(holder, W, H, "panel-3");
    svg.classList.add("focusing");
    const vals = cohorts.flatMap(c => P.flatMap(p => [c.june[p], c.aug[p]]));
    const lo = 0, hi = ceilTo(Math.max(...vals) + 2, 10);
    const labW = narrow ? 0 : 196, valW = narrow ? 0 : 72;
    const x = v => labW + 8 + (v - lo) / (hi - lo) * (W - labW - valW - 16);
    ticks(lo, hi, 10).forEach(t => s("text", { x: x(t), y: 12, "text-anchor": x(t) > W - 12 ? "end" : "middle", class: "axis-txt", text: String(t) }, svg));
    let y = headH, lastGroup = null;
    cohorts.forEach(c => {
      if (c.group && c.group !== lastGroup) { s("text", { x: 0, y: y + 16, class: "axis-lbl", text: c.group }, svg); s("line", { class: "gridline", x1: 0, x2: W, y1: y + 22, y2: y + 22 }, svg); y += groupH; lastGroup = c.group; }
      const trackY = narrow ? y + 32 : y + rowH / 2, textY = narrow ? y + 11 : y + rowH / 2;
      const g = s("g", null, svg);
      s("rect", { class: "hit", x: 0, y, width: W, height: rowH }, g);
      s("line", { class: "gridline", x1: x(lo), x2: x(hi), y1: trackY, y2: trackY }, g);
      s("text", { x: 0, y: textY, "dominant-baseline": "central", class: c.state ? "name-lbl hot" : "name-lbl", text: c.label + (c.small ? " · small subsample" : "") }, g);
      /* the four parties behind, then the one in front, so its marks are never covered */
      P.filter(p => p !== lit).concat([lit]).forEach(p => {
        const a = c.aug[p], j = c.june[p], on = p === lit;
        const m = s("g", { transform: `translate(0,${trackY})`, "data-party": p, class: on ? "lit" : "" }, g);
        marks(m, x, null, j, a, col(p), on ? 5.5 : 4);
        if (on) {
          const d = a - j, right = a >= j;
          s("text", { x: right ? x(a) + 9 : x(a) - 9, y: 0, "dominant-baseline": "central", "text-anchor": right ? "start" : "end", class: "sh-delta " + trend(d), text: signed(d) }, m);
        }
      });
      s("text", { x: W, y: textY, "dominant-baseline": "central", "text-anchor": "end", class: "val-lbl", text: `${c.june[lit]} → ${c.aug[lit]}` }, g);
      Tip.bind(g, `${c.label}${c.small ? ", small subsample" : ""}: ` + P.map(p => `${SHORT[p]} June ${c.june[p]}, August ${c.aug[p]}`).join("; "), () => ({
        head: c.label, sub: (c.state ? "The state · " : c.small ? "Small subsample · " : "") + "June → August, per cent",
        rows: P.map(p => [SHORT[p], `${c.june[p]} → ${c.aug[p]}  (${signed(c.aug[p] - c.june[p])})`, col(p)]),
        flag: c.state ? "" : SUB,
      }));
      y += rowH;
    });
  };

  /* ══════════ 4 · three regions, small multiples on one scale ══════════ */
  CHARTS["panel-4"] = function (holder) {
    const W = widthOf(holder), stacked = under640.matches;
    const gap = 16, tileH = stacked ? 96 : 110, H = stacked ? tileH * 3 + gap * 2 : tileH;
    const svg = root(holder, W, H, "panel-4");
    const tileW = stacked ? W : (W - gap * 2) / 3;
    const hi = ceilTo(Math.max(...C.regions.flatMap(r => [r.june.ONP, r.aug.ONP, r.june.LIB, r.aug.LIB])) + 2, 10);
    C.regions.forEach((r, i) => {
      const ox = stacked ? 0 : i * (tileW + gap), oy = stacked ? i * (tileH + gap) : 0;
      const t = s("g", { transform: `translate(${ox},${oy})` }, svg);
      s("text", { x: 0, y: 12, class: "axis-lbl", text: r.label }, t);
      const labW = 78, valW = 34, x = v => labW + v / hi * (tileW - labW - valW - 6);
      s("line", { class: "axisline", x1: x(0), x2: x(0), y1: 24, y2: tileH - 6 }, t);
      [["ONP", "One Nation"], ["LIB", "Liberal"]].forEach(([p, name], k) => {
        const yc = (stacked ? 42 : 46) + k * (stacked ? 32 : 38), a = r.aug[p], j = r.june[p];
        const g = s("g", null, t);
        s("rect", { class: "hit", x: 0, y: yc - 15, width: tileW, height: 30 }, g);
        s("text", { x: 0, y: yc, "dominant-baseline": "central", class: "name-lbl", text: name }, g);
        s("line", { class: "gridline", x1: x(0), x2: x(hi), y1: yc, y2: yc }, g);
        marks(s("g", { transform: `translate(0,${yc})` }, g), x, null, j, a, col(p));
        s("text", { x: tileW, y: yc, "dominant-baseline": "central", "text-anchor": "end", class: "val-lbl", text: pct(a) }, g);
        Tip.bind(g, `${r.label}, ${name}: August ${a}, June ${j}`, () => ({
          head: `${r.label} · ${name}`, sub: "Primary vote, per cent",
          rows: [["August 2026", pct(a), col(p)], ["June 2026", pct(j)], ["Since June", signed(a - j)]],
          flag: SUB,
        }));
      });
    });
  };

  /* ══════════ 5 · vote flows, a matrix that reads across each row ══════════ */
  CHARTS["panel-5"] = function (holder) {
    const W = widthOf(holder), narrow = phone.matches;
    const cols = P.concat(["RES"]), head = 40, cellH = 48, H = head + C.flows.length * cellH + 8;
    const svg = root(holder, W, H, "panel-5");
    const labW = narrow ? 88 : 190, cw = (W - labW) / cols.length;
    const ABBR = { ALP: "Lab", ONP: "ON", LIB: "Lib", GRN: "Grn", OTH: "Oth", RES: "N/i" };
    const name = d => (d === "RES" ? "not itemised in the report" : SHORT[d]);
    if (!narrow) s("text", { x: 0, y: 12, class: "axis-lbl", text: "March vote" }, svg);
    s("text", narrow ? { x: W, y: 12, "text-anchor": "end", class: "axis-lbl", text: "August → · June beneath" } : { x: labW, y: 12, class: "axis-lbl", text: "August intention →" }, svg);
    cols.forEach((d, i) => {
      const cx = labW + i * cw + cw / 2;
      if (d !== "RES") s("circle", { cx, cy: narrow ? 22 : 26, r: 3.5, fill: col(d) }, svg);
      s("text", { x: cx, y: 36, "text-anchor": "middle", class: "anno", text: narrow ? ABBR[d] : (d === "RES" ? "Not itemised" : SHORT[d]) }, svg);
    });
    C.flows.forEach((r, ri) => {
      const y = head + ri * cellH;
      const g = s("g", null, svg);
      s("rect", { class: "hit", x: 0, y, width: W, height: cellH }, g);
      const lbl = r.name.length > 14 && narrow ? ["Did not vote /", "don't remember"] : [r.name];
      lbl.forEach((t, k) => s("text", { x: 0, y: y + cellH / 2 + (lbl.length > 1 ? (k ? 8 : -7) : 0), "dominant-baseline": "central", class: "name-lbl", text: t }, g));
      cols.forEach((d, i) => {
        const a = d === "RES" ? r.aug.residual : r.aug.cells[d], j = d === "RES" ? r.june.residual : r.june.cells[d];
        const x0 = labW + i * cw, diag = r.party === d;
        const c = s("g", null, g);
        s("rect", { class: "cell", x: x0 + 2, y: y + 3, width: cw - 4, height: cellH - 6, rx: 4 }, c);
        if (diag) s("rect", { class: "cell-diag", x: x0 + 2, y: y + 3, width: cw - 4, height: cellH - 6, rx: 4, stroke: col(d) }, c);
        if (a) s("rect", { x: x0 + 6, y: y + cellH - 11, width: Math.max(2, (cw - 12) * a / 100), height: 4, rx: 2, fill: diag ? col(d) : "var(--muted)" }, c);
        s("text", { x: x0 + 7, y: y + 18, class: "val-lbl", text: a ? String(a) : "—" }, c);
        /* June sits beside the August figure where the cell is wide enough and under it where it is not. */
        if (j) s("text", cw > 64 ? { x: x0 + cw - 6, y: y + 18, "text-anchor": "end", class: "anno", text: `Jun ${j}` } : { x: x0 + 7, y: y + 31, class: "anno", text: String(j) }, c);
        Tip.bind(c, "", () => ({
          head: `${r.name} in March → ${name(d)}`, sub: "Per cent of the row",
          rows: [["August 2026", a ? pct(a) : "none", d === "RES" ? null : col(d)], ["June 2026", j ? pct(j) : "none"]],
          flag: d === "RES" ? "Movement the report does not itemise. It is not a party." : diag ? "The share of the party's March voters it kept." : SUB,
        }), false);
      });
      Tip.bind(g, `${r.name} in March: ` + cols.map(d => { const a = d === "RES" ? r.aug.residual : r.aug.cells[d]; return a ? `${a} per cent to ${name(d)}` : null; }).filter(Boolean).join(", "), () => ({
        head: `${r.name} in March`, sub: "Where they sit in August, per cent of the row",
        rows: cols.map(d => { const a = d === "RES" ? r.aug.residual : r.aug.cells[d], j = d === "RES" ? r.june.residual : r.june.cells[d]; return a || j ? [name(d), `${a ? pct(a) : "none"}${j ? ` · June ${j}` : ""}`, d === "RES" ? null : col(d)] : null; }).filter(Boolean),
        flag: SUB,
      }));
    });
  };

  /* ══════════ 6 · preferred premier, two questions ══════════ */
  const PP_PARTY = { Malinauskas: "ALP", Hurn: "LIB", Bernardi: "ONP" };
  const WORD = ["no", "one", "two", "three", "four", "five", "six"];
  CHARTS["panel-6"] = function (holder) {
    const W = widthOf(holder), stacked = under640.matches;
    const sets = [[C.pp.june, "June 2026", true], [C.pp.aug, "August 2026", false]];
    const rowH = 28, head = 34, gapY = 20, gapX = 32;
    const H = stacked ? sets.reduce((h, [o]) => h + head + o.length * rowH, 0) + gapY : head + Math.max(...sets.map(([o]) => o.length)) * rowH;
    const svg = root(holder, W, H, "panel-6");
    const setW = stacked ? W : (W - gapX) / 2;
    const hi = ceilTo(Math.max(...sets.flatMap(([o]) => o.map(r => r.value))), 10);
    let oy = 0;
    sets.forEach(([opts, title, isJune], si) => {
      const ox = stacked ? 0 : si * (setW + gapX);
      const t = s("g", { transform: `translate(${ox},${stacked ? oy : 0})` }, svg);
      s("text", { x: 0, y: 12, class: "axis-lbl", text: title }, t);
      s("text", { x: 0, y: 27, class: "anno", text: `A question with ${WORD[opts.length]} options` }, t);
      const labW = 92, valW = 40, x = v => labW + v / hi * (setW - labW - valW);
      opts.forEach((o, i) => {
        const yy = head + i * rowH, party = PP_PARTY[o.key], fill = party ? col(party) : "var(--muted)";
        const g = s("g", null, t);
        s("rect", { class: "hit", x: 0, y: yy, width: setW, height: rowH }, g);
        s("text", { x: 0, y: yy + rowH / 2, "dominant-baseline": "central", class: "name-lbl", text: o.label }, g);
        if (isJune) s("rect", { class: "outline", x: labW + 0.75, y: yy + 6.75, width: Math.max(1, x(o.value) - labW - 1.5), height: 14.5, rx: 3, stroke: fill }, g);
        else s("rect", { x: labW, y: yy + 6, width: Math.max(1, x(o.value) - labW), height: 16, rx: 3, fill }, g);
        s("text", { x: x(o.value) + 6, y: yy + rowH / 2, "dominant-baseline": "central", class: "val-lbl", text: pct(o.value) }, g);
        Tip.bind(g, `${title}, ${o.label}: ${o.value} per cent`, () => ({
          head: o.label, sub: `Preferred premier · ${title}`,
          rows: [["Share", pct(o.value), party ? col(party) : null], ["Options offered", String(opts.length)]],
          flag: `The question offered ${opts.map(r => r.label).join(", ")}; it is not compared with the other wave's question.`,
        }));
      });
      oy += head + opts.length * rowH + gapY;
    });
  };

  /* ══════════ 7 · leader ratings ══════════ */
  CHARTS["panel-7"] = function (holder) {
    const W = widthOf(holder), narrow = phone.matches, H = 20 + C.leaders.length * 84;
    const svg = root(holder, W, H, "panel-7");
    const rowLab = narrow ? 50 : 66, netW = 40, neuW = narrow ? 60 : 120, gap = narrow ? 14 : 18;
    const areaW = W - rowLab - neuW - netW - gap * 2, cx = rowLab + areaW / 2;
    const side = ceilTo(Math.max(...C.leaders.flatMap(l => [l.june.pos, l.june.neg, l.aug.pos, l.aug.neg])), 10);
    const k = (areaW / 2 - 24) / side;
    const nx = rowLab + areaW + gap, neuHi = ceilTo(Math.max(...C.leaders.flatMap(l => [l.june.neu, l.aug.neu])), 10), kn = (neuW - 26) / neuHi;
    s("text", { x: cx - 6, y: 11, "text-anchor": "end", class: "axis-lbl", text: narrow ? "neg" : "← negative" }, svg);
    s("text", { x: cx + 6, y: 11, class: "axis-lbl", text: narrow ? "pos" : "positive →" }, svg);
    s("text", { x: nx, y: 11, class: "axis-lbl", text: "neutral" }, svg);
    s("text", { x: W, y: 11, "text-anchor": "end", class: "axis-lbl", text: "net" }, svg);
    s("line", { class: "axisline", x1: cx, x2: cx, y1: 20, y2: H }, svg);
    C.leaders.forEach((l, li) => {
      const by = 20 + li * 84;
      s("text", { x: 0, y: by + 16, class: "name-lbl hot", text: l.name }, svg);
      [["June", l.june, true], ["August", l.aug, false]].forEach(([when, r, isJune], ri) => {
        const yy = by + 26 + ri * 26;
        const g = s("g", null, svg);
        s("rect", { class: "hit", x: 0, y: yy - 3, width: W, height: 24 }, g);
        s("text", { x: 0, y: yy + 9, "dominant-baseline": "central", class: "anno", text: when }, g);
        const bar = (x, w, fill) => isJune
          ? s("rect", { class: "outline", x: x + 0.75, y: yy + 0.75, width: Math.max(1, w - 1.5), height: 16.5, rx: 3, stroke: fill }, g)
          : s("rect", { x, y: yy, width: Math.max(1, w), height: 18, rx: 3, fill }, g);
        bar(cx - r.neg * k, r.neg * k, "var(--sent-neg)");
        bar(cx, r.pos * k, "var(--sent-pos)");
        s("text", { x: cx - r.neg * k - 5, y: yy + 9, "dominant-baseline": "central", "text-anchor": "end", class: "val-lbl", text: String(r.neg) }, g);
        s("text", { x: cx + r.pos * k + 5, y: yy + 9, "dominant-baseline": "central", class: "val-lbl", text: String(r.pos) }, g);
        bar(nx, r.neu * kn, "var(--muted)");
        s("text", { x: nx + r.neu * kn + 5, y: yy + 9, "dominant-baseline": "central", class: "val-lbl", text: String(r.neu) }, g);
        s("text", { x: W, y: yy + 9, "dominant-baseline": "central", "text-anchor": "end", class: "val-lbl", text: signed(r.net) }, g);
        Tip.bind(g, `${l.name}, ${when}: ${r.pos} positive, ${r.neu} neutral, ${r.neg} negative, net ${signed(r.net)}`, () => ({
          head: l.name, sub: `${l.office} · ${when} 2026`,
          rows: [["Positive", pct(r.pos), "var(--sent-pos)"], ["Neutral", pct(r.neu), "var(--muted)"], ["Negative", pct(r.neg), "var(--sent-neg)"], ["Net rating", signed(r.net)]],
          flag: isJune ? "" : `June: net ${signed(l.june.net)}, so ${signed(l.aug.net - l.june.net)} since.`,
        }));
      });
    });
  };

  /* ══════════ 8 · state direction ══════════ */
  CHARTS["panel-8"] = function (holder) {
    const W = widthOf(holder), H = 20 + C.direction.length * 30;
    const svg = root(holder, W, H, "panel-8");
    const labW = 120, valW = 48, hi = ceilTo(Math.max(...C.direction.flatMap(d => [d.june, d.aug])) + 2, 10);
    const x = v => labW + v / hi * (W - labW - valW);
    ticks(0, hi, 10).forEach(t => s("text", { x: x(t), y: 11, "text-anchor": "middle", class: "axis-txt", text: String(t) }, svg));
    C.direction.forEach((d, i) => {
      const yc = 20 + i * 30 + 15;
      const g = s("g", null, svg);
      s("rect", { class: "hit", x: 0, y: yc - 15, width: W, height: 30 }, g);
      s("text", { x: 0, y: yc, "dominant-baseline": "central", class: "name-lbl", text: d.label }, g);
      s("line", { class: "gridline", x1: x(0), x2: x(hi), y1: yc, y2: yc }, g);
      marks(s("g", { transform: `translate(0,${yc})` }, g), x, null, d.june, d.aug, "var(--accent)");
      s("text", { x: W, y: yc, "dominant-baseline": "central", "text-anchor": "end", class: "val-lbl", text: pct(d.aug) }, g);
      Tip.bind(g, `${d.label}: August ${d.aug}, June ${d.june}`, () => ({ head: d.label, sub: "Is the state heading in the right direction?", rows: [["August 2026", pct(d.aug), "var(--accent)"], ["June 2026", pct(d.june)], ["Since June", signed(d.aug - d.june)]], flag: `A one-point move is inside the ±${num(MOE)} margin of error.` }));
    });
  };

  /* ══════════ 9 · biggest issue ══════════ */
  CHARTS["panel-9"] = function (holder) {
    const W = widthOf(holder), narrow = phone.matches, rowH = narrow ? 34 : 22, head = 18;
    const H = head + C.issues.length * rowH;
    const svg = root(holder, W, H, "panel-9");
    const labW = narrow ? 0 : 280, valW = narrow ? 104 : 110;
    const hi = ceilTo(Math.max(...C.issues.map(i => Math.max(i.value, i.june || 0))), 10);
    const x = v => labW + v / hi * (W - labW - valW);
    ticks(0, hi, 10).forEach(t => s("text", { x: x(t), y: 11, "text-anchor": t === 0 && !labW ? "start" : "middle", class: "axis-txt", text: String(t) }, svg));
    C.issues.forEach((it, i) => {
      const yy = head + i * rowH, by = narrow ? yy + 20 : yy + 7;
      const g = s("g", null, svg);
      s("rect", { class: "hit", x: 0, y: yy, width: W, height: rowH }, g);
      s("text", { x: 0, y: narrow ? yy + 9 : yy + rowH / 2, "dominant-baseline": "central", class: "name-lbl", text: it.label }, g);
      s("rect", { x: labW, y: by, width: Math.max(1, x(it.value) - labW), height: 8, rx: 2, fill: "var(--accent)" }, g);
      let end = x(it.value);
      if (it.june) { s("circle", { class: "ring", cx: x(it.june), cy: by + 4, r: 4, stroke: "var(--ink-bold)" }, g); end = Math.max(end, x(it.june) + 5); }
      s("text", { x: end + 6, y: by + 4, "dominant-baseline": "central", class: "val-lbl", text: it.june ? `${it.value}% · June ${it.june}` : `${it.value}%` }, g);
      Tip.bind(g, `${it.label}: ${it.value} per cent${it.june ? `, June ${it.june}` : ""}`, () => ({
        head: it.label, sub: "Biggest issue, per cent of answers",
        rows: [["August 2026", pct(it.value), "var(--accent)"]].concat(it.june ? [["June 2026", pct(it.june)]] : []),
        flag: it.june ? "One of the two categories compared across the waves." : "No June comparison: the category set changed between waves.",
      }));
    });
  };

  /* ══════════ 10 · the fieldwork window ══════════ */
  CHARTS["panel-10"] = function (holder) {
    const W = widthOf(holder), H = 160, narrow = phone.matches;
    const svg = root(holder, W, H, "panel-10");
    const days = C.fieldwork.days, dw = W / days, sy = 92, sh = 24;
    const t0 = Date.parse(C.fieldwork.start + "T00:00:00Z");
    for (let d = 0; d < days; d++) s("rect", { class: "day", x: d * dw, y: sy, width: dw, height: sh }, svg);
    s("rect", { class: "field", x: 0, y: sy, width: W, height: sh }, svg);
    for (let d = 0; d < days; d++) {
      const day = new Date(t0 + d * 86400000).getUTCDate();
      if (d === 0 || d === days - 1 || (day % 5 === 0 && days - 1 - d > 2)) s("text", { x: d * dw + dw / 2, y: sy + sh + 16, "text-anchor": "middle", class: "axis-txt", text: String(day) }, svg);
    }
    s("text", { x: 0, y: H - 6, class: "axis-lbl", text: narrow ? "August · daily responses not published" : "August · DemosAU does not publish the daily distribution of responses" }, svg);
    /* The latest event takes the top row and each label ends at its own mark, so no
       stem ever runs through a label on a lower row. */
    const n = C.events.length;
    C.events.forEach((e, i) => {
      const mx = e.day * dw + dw / 2, ly = 16 + (n - 1 - i) * 24;
      const g = s("g", null, svg);
      s("line", { class: "event", x1: mx, x2: mx, y1: ly + 6, y2: sy + sh }, g);
      const label = s("text", { x: mx - 6, y: ly, "text-anchor": "end", class: "name-lbl", text: `${e.label} · ${e.dayMonth}` }, g);
      let lw = label.getComputedTextLength ? label.getComputedTextLength() : 160;
      const right = lw + 6 <= mx;
      if (!right) { label.setAttribute("x", mx + 6); label.setAttribute("text-anchor", "start"); }
      s("rect", { class: "hit", x: right ? mx - lw - 12 : mx - 6, y: ly - 14, width: lw + 18, height: 22 }, g);
      g.insertBefore(g.lastChild, g.firstChild);
      Tip.bind(g, `${e.label}, ${e.dayMonth}. Source: ${e.source}`, () => ({ head: e.label, sub: e.long, rows: [["Source", e.source]] }));
    });
  };

  /* ══════════ drawing, and redrawing when a chart's width changes ══════════ */
  const widths = new Map();
  function draw(holder) {
    const panel = holder.closest("[data-sa-panel]");
    if (!panel || !CHARTS[panel.id] || holder.offsetParent === null) return;
    try { CHARTS[panel.id](holder); widths.set(holder, holder.clientWidth); }
    catch (e) { console.error("[sa-aug]", panel.id, e); ROOT.classList.remove("sa-js"); }
  }
  const holders = $$(".sa-chart", ROOT);
  holders.forEach(draw);
  if ("ResizeObserver" in window) {
    const ro = new ResizeObserver(entries => entries.forEach(en => {
      const h = en.target, w = h.clientWidth;
      if (w && Math.abs(w - (widths.get(h) || 0)) > 1) { Tip.hide(); draw(h); }
    }));
    holders.forEach(h => ro.observe(h));
  }
})();
